import { styled } from '../../system';
export const createButtonVariant = (colorPrefix, options) => {
    var _a, _b;
    return ({
        $$colorVariantText: `$colors$${colorPrefix}Text`,
        $$colorVariantBg: `$colors$${colorPrefix}Bg`,
        $$colorVariantBorder: `$colors$${colorPrefix}Border`,
        $$colorVariantTextHover: `$colors$${colorPrefix}TextHover`,
        $$colorVariantBgHover: `$colors$${colorPrefix}BgHover`,
        $$colorVariantBorderHover: `$colors$${colorPrefix}BorderHover`,
        $$colorVariantTextActive: `$colors$${colorPrefix}TextActive`,
        $$colorVariantBgActive: `$colors$${colorPrefix}BgActive`,
        $$colorVariantBorderActive: `$colors$${colorPrefix}BorderActive`,
        $$colorVariantTextFocus: `$colors$${colorPrefix}TextFocus`,
        $$colorVariantBgFocus: `$colors$${colorPrefix}BgFocus`,
        $$colorVariantBorderFocus: `$colors$${colorPrefix}BorderFocus`,
        $$colorVariantTextDisabled: `$colors$${colorPrefix}TextDisabled`,
        $$colorVariantBgDisabled: `$colors$${colorPrefix}BgDisabled`,
        $$colorVariantBorderDisabled: `$colors$${colorPrefix}BorderDisabled`,
        $$spinnerTrackBg: (_a = options === null || options === void 0 ? void 0 : options.spinnerTrackColor) !== null && _a !== void 0 ? _a : 'rgba(0, 0, 0, 0.15)',
        color: '$$colorVariantText',
        bgColor: '$$colorVariantBg',
        border: `1px solid $$colorVariantBorder`,
        boxShadow: (_b = options === null || options === void 0 ? void 0 : options.shadow) !== null && _b !== void 0 ? _b : 'none',
        ...((options === null || options === void 0 ? void 0 : options.link) && {
            p: '0 !important',
            minMaxSize: 'auto !important',
            bgColor: 'transparent !important',
            border: 'none !important',
            shadow: 'none !important'
        }),
        '@hover': {
            '&:hover:not(:disabled):not([data-loading="true"])': {
                color: `$$colorVariantTextHover`,
                bgColor: `$$colorVariantBgHover`,
                borderColor: `$$colorVariantBorderHover`
            }
        },
        '&:active:not(:disabled):not([data-loading="true"])': {
            color: `$$colorVariantTextActive`,
            bgColor: `$$colorVariantBgActive`,
            borderColor: `$$colorVariantBorderActive`
        },
        '&:focus:not(:disabled):not([data-loading="true"])': {
            color: `$$colorVariantTextFocus`,
            bgColor: `$$colorVariantBgFocus`,
            borderColor: `$$colorVariantBorderFocus`,
            shadow: options === null || options === void 0 ? void 0 : options.focusShadow
        },
        '&:disabled:not([data-loading="true"])': {
            opacity: 0.5,
            cursor: 'not-allowed',
            color: `$$colorVariantTextDisabled`,
            bgColor: `$$colorVariantBgDisabled`,
            borderColor: `$$colorVariantBorderDisabled`
        }
    });
};
export const StyledButton = styled('button', {
    alignItems: 'center',
    boxSizing: 'border-box',
    userSelect: 'none',
    '&::before': {
        boxSizing: 'border-box'
    },
    '&::after': {
        boxSizing: 'border-box'
    },
    // Custom reset?
    d: 'inline-flex',
    justifyContent: 'center',
    flexShrink: 0,
    lineHeight: '$none',
    shadow: 'none',
    WebkitTapHighlightColor: 'rgba(0,0,0,0)',
    // Custom
    cursor: 'pointer',
    rounded: '$buttonRadii',
    fontFamily: '$sans',
    fontWeight: '$medium',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    variants: {
        size: {
            sm: {
                minMaxH: '$buttonSmHeight',
                px: '$3dot5',
                textSm: true,
                fontSize: '$buttonSmFontSize',
                fontWeight: '$buttonSmFontWeight'
            },
            md: {
                minMaxH: '$buttonMdHeight',
                px: '$4',
                textSm: true,
                fontSize: '$buttonMdFontSize',
                fontWeight: '$buttonMdFontWeight'
            },
            lg: {
                minMaxH: '$buttonLgHeight',
                px: 18,
                textMd: true,
                fontSize: '$buttonLgFontSize',
                fontWeight: '$buttonLgFontWeight'
            },
            xl: {
                minMaxH: '$buttonXlHeight',
                px: '$5',
                textMd: true,
                fontSize: '$buttonXlFontSize',
                fontWeight: '$buttonXlFontWeight'
            },
            '2xl': {
                minMaxH: '$button2XlHeight',
                px: '$7',
                textLg: true,
                fontSize: '$button2XlFontSize',
                fontWeight: '$button2XlFontWeight'
            }
        },
        variant: {
            primary: createButtonVariant('buttonPrimary', {
                shadow: '$xs',
                focusShadow: '$focusRingPrimary',
                spinnerTrackColor: 'rgba(255, 255, 255, 0.3)'
            }),
            secondary: createButtonVariant('buttonSecondary', {
                shadow: '$xs',
                focusShadow: '$focusRingSecondary',
                spinnerTrackColor: 'rgba(255, 255, 255, 0.3)'
            }),
            primaryFaded: createButtonVariant('buttonPrimaryFaded', {
                focusShadow: '$focusRingPrimary'
            }),
            primaryGhost: createButtonVariant('buttonPrimaryGhost', {
                focusShadow: '$focusRingPrimary'
            }),
            primaryLink: createButtonVariant('buttonPrimaryLink', {
                link: true
            }),
            neutral: createButtonVariant('buttonNeutral', {
                shadow: '$xs',
                focusShadow: '$focusRingNeutral'
            }),
            neutralFaded: createButtonVariant('buttonNeutralFaded', {
                focusShadow: '$focusRingNeutral'
            }),
            neutralGhost: createButtonVariant('buttonNeutralGhost', {
                focusShadow: '$focusRingNeutral'
            }),
            neutralLink: createButtonVariant('buttonNeutralLink', {
                link: true
            }),
            danger: createButtonVariant('buttonDanger', {
                shadow: '$xs',
                focusShadow: '$focusRingDanger',
                spinnerTrackColor: 'rgba(255, 255, 255, 0.3)'
            }),
            dangerFaded: createButtonVariant('buttonDangerFaded', {
                focusShadow: '$focusRingDanger'
            }),
            dangerNeutral: createButtonVariant('buttonDangerNeutral', {
                shadow: '$xs',
                focusShadow: '$focusRingDanger'
            }),
            dangerGhost: createButtonVariant('buttonDangerGhost', {
                focusShadow: '$focusRingDanger'
            }),
            dangerLink: createButtonVariant('buttonDangerLink', {
                link: true
            })
        },
        iconButton: {
            true: {
                justifyContent: 'center'
            }
        },
        hasLeftIcon: {
            true: {
                justifyContent: 'space-between'
            }
        },
        hasRightIcon: {
            true: {
                justifyContent: 'space-between'
            }
        },
        isDisabled: {
            true: {}
        },
        isLoading: {
            true: {
                cursor: 'not-allowed',
                justifyContent: 'center'
            }
        },
        fullWidth: {
            true: {
                d: 'flex',
                w: '$full'
            }
        }
    },
    compoundVariants: [
        {
            hasLeftIcon: true,
            size: 'sm',
            css: {
                pl: '$2'
            }
        },
        {
            hasLeftIcon: true,
            size: 'md',
            css: {
                pl: '$2dot5'
            }
        },
        {
            hasLeftIcon: true,
            size: 'lg',
            css: {
                pl: '$3'
            }
        },
        {
            hasLeftIcon: true,
            size: 'xl',
            css: {
                pl: '$3'
            }
        },
        {
            hasLeftIcon: true,
            size: '2xl',
            css: {
                pl: '$4'
            }
        },
        {
            iconButton: true,
            size: 'sm',
            css: {
                minMaxSize: '$buttonSmHeight'
            }
        },
        {
            iconButton: true,
            size: 'md',
            css: {
                minMaxSize: '$buttonMdHeight'
            }
        },
        {
            iconButton: true,
            size: 'lg',
            css: {
                minMaxSize: '$buttonLgHeight'
            }
        },
        {
            iconButton: true,
            size: 'xl',
            css: {
                minMaxSize: '$buttonXlHeight'
            }
        },
        {
            iconButton: true,
            size: '2xl',
            css: {
                minMaxSize: '$button2XlHeight'
            }
        },
        {
            hasRightIcon: true,
            size: 'sm',
            css: {
                pr: '$2'
            }
        },
        {
            hasRightIcon: true,
            size: 'md',
            css: {
                pr: '$2dot5'
            }
        },
        {
            hasRightIcon: true,
            size: 'lg',
            css: {
                pr: '$3'
            }
        },
        {
            hasRightIcon: true,
            size: 'xl',
            css: {
                pr: '$3'
            }
        },
        {
            hasRightIcon: true,
            size: '2xl',
            css: {
                pr: '$4'
            }
        },
        {
            isDisabled: true,
            isLoading: false,
            css: {
                opacity: 0.5,
                cursor: 'not-allowed',
                color: `$$colorVariantTextDisabled`,
                bgColor: `$$colorVariantBgDisabled`,
                borderColor: `$$colorVariantBorderDisabled`,
                '&[href]': {
                    pointerEvents: 'none'
                }
            }
        }
    ],
    defaultVariants: {
        size: 'md',
        variant: 'neutral'
    }
});
